import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { pub, money } from "../lib/crmClient";

export default function PublicPay() {
    const { token } = useParams();
    const [sp] = useSearchParams();
    const [data, setData] = useState(null);
    const [sig, setSig] = useState("");
    const [signing, setSigning] = useState(false);
    const [checkingOut, setCheckingOut] = useState(false);
    const [msg, setMsg] = useState("");

    const load = useCallback(() => {
        pub.get(`/public/invoices/${token}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    }, [token]);
    useEffect(load, [load]);

    // Handle return from Stripe
    useEffect(() => {
        const sessionId = sp.get("session_id");
        const status = sp.get("status");
        if (!sessionId || status !== "success") return;
        setMsg("Confirming payment…");
        const poll = async (n = 0) => {
            if (n > 12) { setMsg("Still processing — refresh in a moment."); return; }
            try {
                const { data } = await pub.get(`/public/payments/status/${sessionId}`);
                if (data.payment_status === "paid") { setMsg("Payment received ✓ Thank you!"); load(); return; }
            } catch { /* ignore */ }
            setTimeout(() => poll(n + 1), 2000);
        };
        poll();
    }, [sp, load]);

    if (!data) return <div className="min-h-screen bg-black text-white flex items-center justify-center font-mono text-[11px] tracking-[0.28em] uppercase text-white/40">Loading…</div>;
    if (data.error) return <div className="min-h-screen bg-black text-white flex items-center justify-center flex-col p-6 text-center">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">404 · Wet paint</div>
        <h1 className="font-display uppercase text-5xl md:text-7xl mt-3 tracking-tight">Link not found.</h1>
    </div>;

    const { invoice, client } = data;
    const isQuote = invoice.kind === "quote";
    const signed = !!invoice.signature_name;
    const paid = invoice.status === "paid";
    const depositPaid = invoice.status === "deposit_paid" || paid;

    const doSign = async (e) => {
        e.preventDefault();
        setSigning(true);
        try {
            await pub.post(`/public/invoices/${token}/sign`, { signature_name: sig });
            load();
        } finally { setSigning(false); }
    };

    const doPay = async (mode) => {
        setCheckingOut(true);
        try {
            const { data } = await pub.post(`/public/invoices/${token}/checkout`, { origin_url: window.location.origin, mode });
            window.location.href = data.checkout_url;
        } catch (err) {
            const d = err?.response?.data?.detail;
            setMsg(typeof d === "string" ? d : "Checkout failed");
            setCheckingOut(false);
        }
    };

    return (
        <div data-testid="public-pay" className="min-h-screen bg-black text-white">
            {/* Header */}
            <header className="border-b border-white/10">
                <div className="max-w-5xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-3">
                        <img src="/favicon.svg" alt="PaintKing" className="w-8 h-8" />
                        <div>
                            <div className="font-display uppercase text-lg leading-none tracking-tight">PaintKing</div>
                            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/50 mt-1">SOFLA · EST. PAUL</div>
                        </div>
                    </a>
                    <a href="tel:5616984044" className="glass-btn font-mono text-[11px] tracking-[0.28em] uppercase px-4 py-2 border border-white/20">561 · 698 · 4044</a>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
                <div className="flex items-baseline justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">{invoice.number} · {isQuote ? "Quote" : "Invoice"}</div>
                        <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mt-2">{invoice.title}</h1>
                        <div className="font-mono text-[12px] text-white/50 mt-2">Issued {new Date(invoice.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Bill to</div>
                        <div className="font-display uppercase text-2xl mt-1">{client?.name}</div>
                        {client?.address && <div className="font-mono text-[11px] text-white/60 mt-1">{client.address}</div>}
                    </div>
                </div>

                {msg && <div data-testid="pay-msg" className="mt-6 border border-[#FFE600]/40 bg-[#FFE600]/[0.06] p-4 font-mono text-[12px] text-[#FFE600]">{msg}</div>}

                <div className="mt-10 border border-white/10">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-white/10 bg-white/[0.02]">
                                {["Description","Qty","Unit","Amount"].map((h) => (
                                    <th key={h} className="text-left p-4 font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {invoice.line_items.map((li, i) => (
                                <tr key={i} className="border-b border-white/5">
                                    <td className="p-4 font-mono text-[13px]">{li.description}</td>
                                    <td className="p-4 font-mono text-[13px]">{li.quantity}</td>
                                    <td className="p-4 font-mono text-[13px]">{money(li.unit_price)}</td>
                                    <td className="p-4 font-mono text-[13px] text-right">{money(li.quantity * li.unit_price)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-6 flex flex-col items-end gap-1 font-mono text-[14px]">
                    <div className="flex gap-8"><span className="text-white/60">Subtotal</span><span>{money(invoice.subtotal)}</span></div>
                    <div className="flex gap-8"><span className="text-white/60">Tax ({(invoice.tax_rate*100).toFixed(2)}%)</span><span>{money(invoice.tax_amount)}</span></div>
                    <div className="flex gap-8 mt-2 pt-2 border-t border-white/10"><span className="text-[#FFE600] font-display uppercase tracking-widest text-[11px]">Total</span><span className="font-display text-3xl">{money(invoice.total)}</span></div>
                    {invoice.deposit_amount > 0 && <div className="flex gap-8"><span className="text-white/60">Deposit due</span><span className="text-[#FFE600]">{money(invoice.deposit_amount)}</span></div>}
                    <div className="flex gap-8"><span className="text-white/60">Balance</span><span>{money(invoice.balance_due)}</span></div>
                </div>

                {invoice.notes && (
                    <div className="mt-8 glass border border-white/10 p-5">
                        <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">Note from Paul</div>
                        <p className="font-mono text-[13px] leading-relaxed text-white/85 mt-2">{invoice.notes}</p>
                    </div>
                )}

                {/* Sign */}
                <section data-testid="pay-sign-section" className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="glass border border-white/10 p-6">
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">Step 1 · Sign</div>
                        <h3 className="font-display uppercase text-2xl md:text-3xl leading-tight tracking-tight mt-2">
                            {signed ? "Signed ✓" : "Type your name to sign."}
                        </h3>
                        {signed ? (
                            <div className="mt-4">
                                <div className="font-display text-4xl italic">{invoice.signature_name}</div>
                                <div className="font-mono text-[11px] text-white/50 mt-2">Signed {new Date(invoice.signed_at).toLocaleString()}</div>
                            </div>
                        ) : (
                            <form onSubmit={doSign} className="mt-5 space-y-3">
                                <input required minLength={2} data-testid="pay-sign-input" placeholder="Full name" value={sig} onChange={(e) => setSig(e.target.value)}
                                    className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-4 py-3 font-display text-2xl italic text-white placeholder-white/25"/>
                                <button type="submit" disabled={signing} data-testid="pay-sign-btn"
                                    className="w-full border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-4 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-50">
                                    {signing ? "Signing…" : "Sign & agree →"}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Pay */}
                    <div className={`glass border p-6 ${signed ? "border-white/10" : "border-white/10 opacity-60 pointer-events-none"}`}>
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">Step 2 · Pay</div>
                        <h3 className="font-display uppercase text-2xl md:text-3xl leading-tight tracking-tight mt-2">
                            {paid ? "Paid in full ✓" : depositPaid ? "Deposit received ✓" : isQuote ? "Approve deposit" : "Pay online"}
                        </h3>
                        {!paid && !isQuote && (
                            <div className="mt-5 space-y-3">
                                {!depositPaid && invoice.deposit_amount > 0 && (
                                    <button onClick={() => doPay("deposit")} disabled={checkingOut} data-testid="pay-deposit-btn"
                                        className="w-full border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-4 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-50">
                                        {checkingOut ? "Redirecting…" : `Pay deposit — ${money(invoice.deposit_amount)}`}
                                    </button>
                                )}
                                <button onClick={() => doPay("full")} disabled={checkingOut} data-testid="pay-full-btn"
                                    className="w-full border border-white/40 hover:border-white transition-colors px-4 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-50">
                                    {checkingOut ? "Redirecting…" : `Pay ${depositPaid ? "balance" : "in full"} — ${money(depositPaid ? invoice.balance_due : invoice.total)}`}
                                </button>
                                <div className="font-mono text-[10px] text-white/40 text-center">Secured by Stripe · Card & Apple Pay</div>
                            </div>
                        )}
                        {isQuote && !depositPaid && invoice.deposit_amount > 0 && (
                            <div className="mt-5">
                                <button onClick={() => doPay("deposit")} disabled={checkingOut} data-testid="pay-deposit-btn"
                                    className="w-full border border-[#FFE600] text-[#FFE600] hover:bg-[#FFE600] hover:text-black transition-colors px-4 py-3 font-mono text-[11px] tracking-[0.3em] uppercase disabled:opacity-50">
                                    {checkingOut ? "Redirecting…" : `Approve & pay deposit — ${money(invoice.deposit_amount)}`}
                                </button>
                            </div>
                        )}
                    </div>
                </section>

                <footer className="mt-14 pt-6 border-t border-white/10 flex items-center justify-between flex-wrap gap-4">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40">Paul · PaintKing · Delray Beach, FL</div>
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/60">561 · 698 · 4044 · paintking.com</div>
                </footer>
            </div>
        </div>
    );
}
